import { type AnyAction } from 'redux';
import actions from './actions';
import { DATE_FORMAT } from '@/constants/dates';
import { type RootState } from '@/types/app';
import { type IMeState } from '@/types/me';
import { formatDate } from '@/utils/dates';
import { toggleBodyScroll } from '@/utils/global';

export const initialState: IMeState = {
  data: null,
  meConnectionError: false,
  error: false,
  loading: false,
  cardsLoading: 'init',
  cardDeleting: false,
  favoriteLocations: null,
  favoriteLocationsLoading: false,
  favoriteLocationsConnectionError: false,
  favoriteOrders: [],
  favoriteOrdersLoading: false,
  favoriteOrdersConnectionError: false,
  pastOrders: [],
  reorderModalOpen: false,
  paymentInfoLoading: false,
  cards: [],
  allLocationsCards: [],
  orderDetails: null,
  myOffers: [],
  myAddresses: [],
  storedAddressesMenuOpen: false,
  storedAddressId: null,
  // Punchh V2 only
  loyaltyState: null,
  loyaltyConnectionError: false,
  loyaltyStateLoading: false,
  loyaltyStateFailed: false,
  redeemables: [],
  messages: null,
  rewardsHistory: [],
  redemptions: [],
  accountDropdownOpen: false,
  activeRedemption: null,
  receiptBarcodeLoading: false,
  favoriteLocationFormOpen: false,
  favoriteLocationMenuOpen: false,
  favoriteLocationId: null,
};

export const me = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case actions.FETCH_LOYALTY_STATE:
      return Object.assign({}, state, {
        error: false,
        loading: true,
        loyaltyStateLoading: true,
        loyaltyStateFailed: false,
      });
    case actions.FETCH_ME:
    case actions.FETCH_REDEEMABLES:
    case actions.FETCH_MESSAGES:
    case actions.FETCH_REWARDS_HISTORY:
    case actions.FETCH_REDEMPTIONS:
    case actions.CREATE_REDEMPTION:
      // Deliberately does NOT clear `loyaltyConnectionError`. The rewards page's "Try again"
      // dispatches these, and with `loyaltyState` still null clearing the flag dropped the page
      // to a blank render for the whole retry while the other loyalty fetches' toasts showed
      // alone (code review run 5) — the favorite-orders trap again. The loyalty-state fetch's
      // own outcomes lower it: FETCH_LOYALTY_STATE_SUCCESS and FETCH_LOYALTY_STATE_FAIL.
      return Object.assign({}, state, {
        error: false,
        loading: true,
      });
    case actions.FETCH_ME_FAVORITE_LOCATIONS:
      return Object.assign({}, state, {
        error: false,
        favoriteLocationsLoading: true,
        favoriteLocationsConnectionError: false,
      });
    case actions.CLEAR_ME:
      return initialState;
    case actions.ME_SUCCESS:
      const successResult = Object.assign({}, state, {
        data: action.data,
        loading: false,
        meConnectionError: false,
      });
      return successResult;
    case actions.UPDATE_ME_FAIL:
      const result = Object.assign({}, state, {
        loading: false,
      });
      return result;
    case actions.FETCH_ME_ORDER_DETAILS_SUCCESS:
      return Object.assign({}, state, {
        orderDetails: action.orderDetails,
      });
    case actions.CLEAR_ME_ORDER_DETAILS:
      return Object.assign({}, state, {
        orderDetails: null,
      });
    case actions.ME_FAVORITE_LOCATIONS_SUCCESS:
      return Object.assign({}, state, {
        favoriteLocations: action.favoriteLocations,
        favoriteLocationsLoading: false,
        favoriteLocationsConnectionError: false,
      });
    case actions.ME_FAVORITE_LOCATIONS_CONNECTION_ERROR:
      return Object.assign({}, state, {
        favoriteLocationsLoading: false,
        favoriteLocationsConnectionError: true,
      });
    // Deliberately does NOT clear `favoriteOrdersConnectionError`. The retry that the
    // connection panel offers dispatches this action, and clearing the flag here dropped
    // the diner through to the in-flight gate below — the section went blank for the
    // whole request and the panel they had just clicked vanished (TEAM-14780 UAT A2, and
    // the same blank-section class Ian flagged). The flag is lowered by whichever
    // terminal action follows: SUCCESS, or FAIL on a non-connection failure.
    case actions.FETCH_ME_FAVORITE_ORDERS:
      return Object.assign({}, state, {
        favoriteOrdersLoading: true,
      });
    case actions.FETCH_ME_FAVORITE_ORDERS_SUCCESS:
      return Object.assign({}, state, {
        favoriteOrders: action.favoriteOrders,
        favoriteOrdersLoading: false,
        favoriteOrdersConnectionError: false,
      });
    case actions.FETCH_ME_FAVORITE_ORDERS_CONNECTION_ERROR:
      return Object.assign({}, state, {
        favoriteOrdersLoading: false,
        favoriteOrdersConnectionError: true,
      });
    // Every other failure (a real status code, or a non-JSON body) has to clear the
    // in-flight flag too: `FETCH_ME_FAVORITE_ORDERS` raised it, and the list's in-flight
    // gate renders nothing while it is up. Without this case a 401/5xx left it raised for
    // the rest of the session and the whole Favorite Orders section — header and empty
    // state included — stayed blank behind a dismiss-only toast (TEAM-14780).
    // `favoriteLocationsLoading` above carries the same gap and is harmless only because
    // no render reads it; check that before copying either pattern.
    case actions.FETCH_ME_FAVORITE_ORDERS_FAIL:
      return Object.assign({}, state, {
        favoriteOrdersLoading: false,
        favoriteOrdersConnectionError: false,
      });
    case actions.FETCH_ME_PAST_ORDERS_SUCCESS:
      return Object.assign({}, state, {
        pastOrders: action.pastOrders,
      });
    case actions.TOGGLE_REORDER_RECEIPT:
      return Object.assign({}, state, {
        reorderModalOpen: action.show,
      });
    case actions.FETCH_ADDRESSES_SUCCESS:
      return Object.assign({}, state, {
        myAddresses: action.myAddresses,
      });
    case actions.TOGGLE_STORED_ADDRESSES_MENU:
      return Object.assign({}, state, {
        storedAddressesMenuOpen: action.show,
      });
    case actions.SET_ACTIVE_STORED_ADDRESS:
      return Object.assign({}, state, {
        storedAddressId: action.addressId,
      });
    case actions.FETCH_ALL_LOCATIONS_CREDIT_CARDS:
      return Object.assign({}, state, {
        paymentInfoLoading: true,
      });
    case actions.FETCH_ALL_LOCATIONS_CREDIT_CARDS_SUCCESS:
      return Object.assign({}, state, {
        paymentInfoLoading: false,
        allLocationsCards: action.allLocationsCards,
      });
    case actions.ME_FAIL:
      return Object.assign({}, state, {
        error: true,
        loading: false,
        // Two sagas dispatch ME_FAIL: the profile fetch (tagged `fromProfile`) and
        // favorite-locations. Only the profile's own failure says anything about the profile.
        meConnectionError: action.fromProfile ? false : state.meConnectionError,
      });
    // A network-layer profile failure (TEAM-14780). Only terminal outcomes of the profile
    // fetch lower the flag — ME_SUCCESS, the profile's own ME_FAIL (a real status code: not a
    // connection problem) and ME_STOP_LOADING (no loyalty token / no user). FETCH_ME deliberately does not, so the /account panel
    // stays on screen while its own "Try again" is in flight instead of dropping to blank
    // sections for the length of the request (the same trap as favorite orders, UAT A2).
    case actions.ME_CONNECTION_ERROR:
      return Object.assign({}, state, {
        error: true,
        loading: false,
        meConnectionError: true,
      });
    case actions.ME_STOP_LOADING:
      return Object.assign({}, state, {
        loading: false,
        meConnectionError: false,
      });
    case actions.UPDATE_ME:
      return Object.assign({}, state, {
        loading: true,
      });
    case actions.UPDATE_ME_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
      });
    case actions.FETCH_OFFERS_SUCCESS:
      return Object.assign({}, state, {
        myOffers: action.rewards,
      });
    case actions.APPEND_USER_PHONE:
      return Object.assign({}, state, {
        data: Object.assign({}, state.data, {
          phone: action.phone,
        }),
      });
    // Punchh V2 only
    case actions.FETCH_LOYALTY_STATE_SUCCESS:
      return Object.assign({}, state, {
        loyaltyState: action.data,
        loading: false,
        loyaltyConnectionError: false,
        loyaltyStateLoading: false,
        loyaltyStateFailed: false,
      });
    case actions.FETCH_LOYALTY_STATE_CONNECTION_ERROR:
      return Object.assign({}, state, {
        loading: false,
        loyaltyConnectionError: true,
        loyaltyStateLoading: false,
        loyaltyStateFailed: true,
      });
    // A real status failure is not a connection problem, so it must not leave an earlier
    // connection panel ("check your connection") standing.
    case actions.FETCH_LOYALTY_STATE_FAIL:
      return Object.assign({}, state, {
        loading: false,
        loyaltyConnectionError: false,
        loyaltyStateLoading: false,
        loyaltyStateFailed: true,
      });
    case actions.FETCH_REDEEMABLES_SUCCESS:
      return Object.assign({}, state, {
        redeemables: action.redeemables,
        loading: false,
      });
    case actions.FETCH_MESSAGES_SUCCESS:
      return Object.assign({}, state, {
        messages: action.messages,
      });
    case actions.MARK_MESSAGE_AS_READ:
      return {
        ...state,
        messages: state.messages
          ? state.messages.map((message) => {
              if (message.id !== action.messageId) {
                return message;
              }

              return {
                ...message,
                read_at: formatDate(null, DATE_FORMAT.ISO_DATE),
              };
            })
          : null,
      };
    case actions.FETCH_REWARDS_HISTORY_SUCCESS:
      return Object.assign({}, state, {
        rewardsHistory: action.rewardsHistory,
        loading: false,
      });
    case actions.FETCH_REDEMPTIONS_SUCCESS:
      return Object.assign({}, state, {
        redemptions: action.redemptions,
      });
    case actions.CREATE_REDEMPTION_SUCCESS:
      return Object.assign({}, state, {
        activeRedemption: action.activeRedemption,
        loading: false,
      });
    case actions.CREATE_REDEMPTION_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        error: true,
      });
    case actions.CLEAR_ACTIVE_REDEMPTION:
      return Object.assign({}, state, {
        activeRedemption: null,
      });
    case actions.TOGGLE_ACCOUNT_DROPDOWN:
      const accountDropdownOpen =
        typeof action.show !== 'undefined' ? action.show : !state.accountDropdownOpen;

      // Disable / enable body scrolling
      toggleBodyScroll(!accountDropdownOpen);

      return Object.assign({}, state, {
        accountDropdownOpen:
          typeof action.show !== 'undefined' ? action.show : !state.accountDropdownOpen,
      });
    case actions.REDEEM_RECEIPT_BARCODE:
      return Object.assign({}, state, {
        receiptBarcodeLoading: true,
      });
    case actions.REDEEM_RECEIPT_BARCODE_SUCCESS:
    case actions.REDEEM_RECEIPT_BARCODE_FAILURE:
      return Object.assign({}, state, {
        receiptBarcodeLoading: false,
      });
    default:
      return state;
  }
};

export const meSliceSelector = (state: RootState) => state.app.me;
